import { useState, memo, useMemo } from 'react';
import { Common } from '@stockbit/ui';

import If from 'global/ErrorHandlers/If';
import { trackAutoBuyOrderCanceled } from 'lib/analytics/autoOrder';
import { ORDER_TYPE } from 'constants/trading';
import tradingModalStore from 'global/TradingModal/store';
import useCashInfoStore from 'global/Securities/stores/useCashInfoStore';
import { TransactionFormType } from 'typings/transactionForm';
import useUIConfigStore from 'stores/useUIConfigStore';
import {
  PaymentMethod,
  TransactionType,
} from 'global/Securities/types/transaction';
import { PortfolioItem } from 'global/Securities/types/api/portfolio';
import { useFliptBoolean } from 'lib/flipt/hooks';
import { FeatureFlagKeys } from 'lib/flipt/constants/flagKeys';

import tradingDataStore from 'lib/stores/tradingData';
import { EmittenInfoResponse } from 'global/Securities/types/api/emittenInfo';
import { StockType } from 'global/Securities/types/stockType';
import TableOrderbook from '../../TableOrderbook';
import FormHeader from '../../components/Header';
import FormBuy from './FormBuy';
import FormSell from './FormSell';
import ExerciseForm from './FormExercise';
import {
  ModalFormWrapper,
  TransactionModalContainer,
  FormWrapper,
  FreezeLayer,
} from '../../styled';
import realTradingStore from '../store';
import { useBondsModalStore } from '../ModalBonds/stores/useBondsModalStore';
import useBondsAnalytics from '../ModalBonds/useBondsAnalytics';

const { Flex } = Common;

type BuySellModalProps = {
  visible: boolean;
  showOrderTypeModal: () => void;
  showAutoBuyTncModal: () => void;
  showSpecialMonitoringModal: () => void;
  onUpdateSymbol: (symbol) => void;
  onShowTradingLimitTnc?(): void;
  onShowSplitOrderInfo?(): void;
  onShowSplitOrderMethodInfo?(): void;
  onSubmit: () => void;
  closeModal: () => void;
  // eslint-disable-next-line react/require-default-props
  onSwitchTab?: (prop: string) => void;
  portfolioData: PortfolioItem;
  onShowDayTradeOnboarding?(): void;
  onShowDayTradeUnavailable?(): void;
};

const transactionToOrderType = {
  [TransactionType.Buy]: ORDER_TYPE.BUY,
  [TransactionType.Sell]: ORDER_TYPE.SELL,
};

function TransactionModal({
  visible,
  showAutoBuyTncModal,
  showOrderTypeModal,
  showSpecialMonitoringModal,
  onUpdateSymbol,
  onShowTradingLimitTnc = () => {},
  onShowSplitOrderInfo = () => {},
  onShowSplitOrderMethodInfo = () => {},
  onSubmit,
  closeModal,
  onSwitchTab = () => {},
  portfolioData,
  onShowDayTradeOnboarding = () => {},
  onShowDayTradeUnavailable = () => {},
}: BuySellModalProps) {
  const isFastOrderEnabled = useFliptBoolean(FeatureFlagKeys.FAST_ORDER);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const resetCashInfo = useCashInfoStore((s) => s.resetCashInfo);
  const { FRBondActionTrackers } = useBondsAnalytics();

  const { orderType, showOrderbook, showTabs } = tradingModalStore();
  const resetOrderData = realTradingStore((s) => s.resetOrderData);
  const setUIValue = useUIConfigStore((s) => s.setUIValue);
  const openBondsBuyModal = useBondsModalStore((s) => s.openBuyModal);
  const company = tradingDataStore(
    (s) => s.company.data as EmittenInfoResponse,
  );

  const available_lot = portfolioData?.available_lot || 0;

  const isBuy =
    orderType === ORDER_TYPE.BUY || orderType === ORDER_TYPE.AMEND_BUY;
  const isSell =
    orderType === ORDER_TYPE.SELL || orderType === ORDER_TYPE.AMEND_SELL;
  const isExercise = orderType === ORDER_TYPE.EXERCISE;
  const isAmend = useMemo(
    () => [ORDER_TYPE.AMEND_BUY, ORDER_TYPE.AMEND_SELL].includes(orderType),
    [orderType],
  );

  const isFormTypeSelectVisible = useMemo(
    () =>
      isFastOrderEnabled &&
      [ORDER_TYPE.BUY, ORDER_TYPE.SELL].includes(orderType) &&
      [
        StockType.Saham,
        StockType.Waran,
        StockType.Right,
        StockType.Etf,
      ].includes(company?.type_company.toLowerCase() as StockType),
    [isFastOrderEnabled, isAmend],
  );

  const defaultTabValue = useMemo(() => {
    if (
      available_lot <= 0 ||
      [(ORDER_TYPE.BUY, ORDER_TYPE.AMEND_BUY)].includes(orderType)
    ) {
      return TransactionType.Buy;
    }
    if ([ORDER_TYPE.SELL, ORDER_TYPE.AMEND_SELL].includes(orderType)) {
      return TransactionType.Sell;
    }

    return TransactionType.Buy;
  }, [orderType]);

  const handleUpdateSymbol = (selected: string, isBonds?: boolean) => {
    if (isBonds) {
      openBondsBuyModal(selected);
      FRBondActionTrackers.onOrderButtonClicked(
        'Buy',
        selected,
        'Search Company Page',
      );
      closeModal();
      return;
    }
    onUpdateSymbol(selected);
    setIsSearchActive(false);
  };

  const handleClose = () => {
    resetCashInfo();
    closeModal();
    resetOrderData();
    trackAutoBuyOrderCanceled();

    setUIValue('transactionPaymentMethod', PaymentMethod.TradingBalance);
  };

  const handleFormTypeChange = (value: TransactionFormType) => {
    if (value === TransactionFormType.FastOrder) handleClose();
  };

  const handleSwitchTab = (value: TransactionType) => {
    onSwitchTab(transactionToOrderType[value]);
  };

  return (
    <>
      <TransactionModalContainer
        width="auto"
        data-cy="transaction-modal-real"
        visible={visible}
        onCancel={handleClose}
      >
        <Flex>
          <If condition={showOrderbook && !isExercise}>
            <TableOrderbook />
          </If>
          <ModalFormWrapper>
            {isSearchActive && (
              <FreezeLayer onClick={() => setIsSearchActive(false)} />
            )}
            <FormWrapper>
              <FormHeader
                orderType={orderType}
                defaultTabsValue={defaultTabValue}
                showTabs={showTabs}
                isSearchActive={isSearchActive}
                disableTabs={available_lot <= 0}
                setIsSearchActive={() => setIsSearchActive(!isSearchActive)}
                formTypeSelectVisible={isFormTypeSelectVisible}
                onUpdateSymbol={handleUpdateSymbol}
                onFormTypeChange={handleFormTypeChange}
                onTabsChange={handleSwitchTab}
              />

              {isBuy && (
                <FormBuy
                  showOrderTypeModal={showOrderTypeModal}
                  showAutoBuyTncModal={showAutoBuyTncModal}
                  showSpecialMonitoringModal={showSpecialMonitoringModal}
                  onShowTradingLimitTnc={onShowTradingLimitTnc}
                  onShowSplitOrderInfo={onShowSplitOrderInfo}
                  onShowSplitOrderMethodInfo={onShowSplitOrderMethodInfo}
                  onSubmit={onSubmit}
                  onShowDayTradeOnboarding={onShowDayTradeOnboarding}
                  onShowDayTradeUnavailable={onShowDayTradeUnavailable}
                />
              )}

              {isSell && (
                <FormSell
                  showSpecialMonitoringModal={showSpecialMonitoringModal}
                  onShowSplitOrderInfo={onShowSplitOrderInfo}
                  onShowSplitOrderMethodInfo={onShowSplitOrderMethodInfo}
                  onSubmit={onSubmit}
                  onShowDayTradeUnavailable={onShowDayTradeUnavailable}
                />
              )}
              {isExercise && <ExerciseForm onSubmit={onSubmit} />}
            </FormWrapper>
          </ModalFormWrapper>
        </Flex>
      </TransactionModalContainer>
    </>
  );
}

export default memo(TransactionModal);
